import { groupLayerMarkers } from '../modules/layer-screen-clusters';
import { drillDownTransactionCluster } from '../modules/transaction-drilldown';
import GeoJsonFetchLayer from './GeoJsonFetchLayer';
import { MarkerGroup, createHtmlMarker } from '../utils/markers';
import { removeSourceAndLayers } from '../utils/maplibre-helpers';

// Copper, deliberately far from the teal of user listings and the blue of
// transactions: the three layers can be switched on at the same time and a
// developer's asking price must never be mistaken for a notarised price.
var OFFER_AGG_COLOR = 'rgba(183,62,29,0.92)';

function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (char) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
    });
}

function bboxParamsFromString(bbox) {
    var parts = String(bbox || '').split(',').map(function (value) {
        return parseFloat(value);
    });
    if (parts.length !== 4 || parts.some(function (value) { return !isFinite(value); })) {
        return {};
    }
    return {
        min_lng: parts[0],
        min_lat: parts[1],
        max_lng: parts[2],
        max_lat: parts[3],
    };
}

function queryString(params) {
    var search = new URLSearchParams();
    Object.keys(params || {}).forEach(function (key) {
        var value = params[key];
        if (value == null || value === '') {
            return;
        }
        search.set(key, value);
    });
    return search.toString();
}

function formatPriceCompact(value) {
    var num = Number(value);
    if (value == null || value === '' || !isFinite(num)) {
        return 'Brak ceny';
    }
    if (num >= 1000000) {
        return (num / 1000000).toLocaleString('pl-PL', { maximumFractionDigits: 2 }) + ' mln zł';
    }
    if (num >= 100000) {
        return Math.round(num / 1000).toLocaleString('pl-PL') + ' tysięcy złotych';
    }
    return num.toLocaleString('pl-PL', { maximumFractionDigits: 0 }) + ' zł';
}

function formatPricePerM2(value) {
    var num = Number(value);
    if (value == null || value === '' || !isFinite(num)) {
        return '';
    }
    return Math.round(num).toLocaleString('pl-PL') + ' zł/m²';
}

/** An approximate point must look different from a matched address point. */
function isApproximate(geometrySource) {
    return geometrySource !== 'address_point';
}

function buildAggHtml(count) {
    var size = count < 10 ? 11 : count < 100 ? 14 : count < 1000 ? 17 : 20;
    var diameter = size * 2;
    return '<div class="nm-agg-dot nm-agg-dot--developer-offer" style="width:' + diameter
        + 'px;height:' + diameter + 'px;background:' + OFFER_AGG_COLOR + '">'
        + '<span class="nm-agg-count">' + count + '</span></div>';
}

function buildMarkerHtml(props) {
    var approximateClass = isApproximate(props.geometry_source) ? ' nm-developer-offer-marker--approximate' : '';
    var id = props.investment_id == null ? '' : String(props.investment_id);
    var perM2 = formatPricePerM2(props.price_per_m2_median);
    return '<div class="nm-developer-offer-marker' + approximateClass + '"'
        + ' data-developer-offer-investment-id="' + escapeHtml(id) + '">'
        + '<span class="nm-developer-offer-marker__count">' + escapeHtml(String(props.count || 0)) + '</span>'
        + '<span class="nm-developer-offer-marker__price">'
        + escapeHtml(perM2 || formatPriceCompact(props.price_min))
        + '</span></div>';
}

/**
 * Public asking prices imported from developers.
 *
 * One marker stands for a whole investment, never for a single flat: hundreds of
 * units share the same coordinates, so per-unit markers would both lie about the
 * density of the map and hide each other.
 */
export default class DeveloperOffersLayer extends GeoJsonFetchLayer {
    static ID = 'ofertyDeweloperow';
    static DESCRIPTION = 'Oferty deweloperów';
    static MIN_ZOOM = 0;
    static SOURCE_ID = 'src-developer-offers';
    static BASE_LAYER_IDS = [];
    static RENDER_ORDER_LAYER_IDS = [];

    /**
     * Investment features carry no `properties.id`, so the inherited default
     * returns null and the keyed marker diff skips every feature — the layer
     * fetches fine and draws nothing. The investment identifier is the key.
     */
    getFeatureKey(feature) {
        const props = feature && feature.properties;
        return props && props.investment_id != null ? props.investment_id : null;
    }

    getSourceId() {
        return DeveloperOffersLayer.SOURCE_ID;
    }

    getLayerIds() {
        return DeveloperOffersLayer.BASE_LAYER_IDS;
    }

    getRenderOrderLayerIds() {
        return DeveloperOffersLayer.RENDER_ORDER_LAYER_IDS;
    }

    buildUrl() {
        var params = Object.assign(
            {
                zoom: this.config.getCurrentZoom(),
                limit: 750,
            },
            bboxParamsFromString(this.config.getCurrentBbox ? this.config.getCurrentBbox(0.35) : ''),
            this.config.getDeveloperOfferFilterParams ? this.config.getDeveloperOfferFilterParams() : {}
        );

        return this.config.baseUrl + 'api/developer-offers/geojson?' + queryString(params);
    }

    getFilterFingerprint() {
        var params = this.config.getDeveloperOfferFilterParams
            ? this.config.getDeveloperOfferFilterParams()
            : {};
        return JSON.stringify(params || {});
    }

    renderAggregatedData(data) {
        var map = this.getMap();
        if (!map) {
            return null;
        }

        var self = this;
        var group = new MarkerGroup();
        groupLayerMarkers(data.features, map, data.aggregated === true).forEach(function (feature) {
            if (!feature || !feature.geometry || feature.geometry.type !== 'Point') {
                return;
            }
            if (feature.properties?.aggregated !== true) {
                const marker = self.createFeatureMarker(feature);
                if (marker) group.add(marker);
                return;
            }
            var props = feature.properties || {};
            var coords = feature.geometry.coordinates;
            // The badge counts offers, not markers: one bucket can hold a single
            // investment with three hundred flats.
            var count = Number(props.count || 0);
            if (!count) {
                return;
            }
            group.add(createHtmlMarker(
                coords,
                buildAggHtml(count),
                'nm-developer-offer-agg-wrap',
                {
                    onClick: function () {
                        drillDownTransactionCluster(map, feature);
                    },
                }
            ));
        });

        group.addTo(map);
        return group;
    }

    createFeatureMarker(feature) {
        if (!feature || !feature.geometry || feature.geometry.type !== 'Point') {
            return null;
        }
        var self = this;
        var props = feature.properties || {};
        var coords = feature.geometry.coordinates;
        return createHtmlMarker(
            coords,
            buildMarkerHtml(props),
            'nm-developer-offer-marker-wrap',
            {
                onClick: function () {
                    if (self.config.onSelectDeveloperInvestment) {
                        self.config.onSelectDeveloperInvestment(props, { lat: coords[1], lng: coords[0] });
                    }
                },
            }
        );
    }

    renderIndividualData(data) {
        var map = this.getMap();
        if (!map) {
            return null;
        }

        var self = this;
        var group = new MarkerGroup();
        (data.features || []).forEach(function (feature) {
            var marker = self.createFeatureMarker(feature);
            if (marker) {
                group.add(marker);
            }
        });

        group.addTo(map);
        return group;
    }

    renderData(data) {
        return this.renderCapacityMarkerData(data);
    }

    clear() {
        this.abortPending();
        this.resetAccumulator();
        this.disposeKeyedMarkerGroup();

        var map = this.getMap();
        var layer = this.config.getLayer ? this.config.getLayer() : null;
        if (layer && typeof layer.remove === 'function') {
            layer.remove();
        }
        if (map) {
            removeSourceAndLayers(map, this.getSourceId(), this.getLayerIds());
        }
        if (this.config.setLayer) {
            this.config.setLayer(null);
        }
        if (this.config.onCleared) {
            this.config.onCleared(this);
        }
    }
}
